import type { ActionPriority, ActionStatus, CalendarEventType } from '../../../shared/types.ts';

const TZ = 'America/Sao_Paulo';

const numberFormat = new Intl.NumberFormat('pt-BR');
const compactFormat = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });

export const formatNumber = (value: number): string => numberFormat.format(Math.round(value));
export const formatCompact = (value: number): string => compactFormat.format(value);

export function formatPercent(value: number, digits = 1): string {
  return `${value.toFixed(digits).replace('.', ',')}%`;
}

/** Datas do banco são YYYY-MM-DD sem fuso — fixar em UTC evita o off-by-one. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = iso.length === 10 ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, dateStyle: 'short' }).format(date);
}

export function formatDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = iso.length === 10 ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ,
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

/** Hoje no fuso da equipe, como YYYY-MM-DD. */
export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
}

export function daysUntil(isoDate: string): number {
  const target = new Date(`${isoDate}T12:00:00Z`).getTime();
  const today = new Date(`${todayIso()}T12:00:00Z`).getTime();
  return Math.round((target - today) / 86_400_000);
}

/** Dias entre duas datas YYYY-MM-DD (positivo quando `to` é posterior). */
export function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to.slice(0, 10)}T12:00:00Z`).getTime() -
      new Date(`${from.slice(0, 10)}T12:00:00Z`).getTime()) /
      86_400_000,
  );
}

/** "hoje", "ontem" ou "há N dias" — deixa claro o quão velho é um número sem exigir conta de cabeça. */
export function describeAge(isoDate: string): string {
  const days = daysBetween(isoDate, todayIso());
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `há ${days} dias`;
}

export function formatSigned(value: number): string {
  return `${value >= 0 ? '+' : '−'}${formatNumber(Math.abs(value))}`;
}

export function formatSignedPercent(value: number, digits = 1): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(digits).replace('.', ',')}%`;
}

export function describeDeadline(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const diff = daysUntil(isoDate);
  if (diff < 0) return `Atrasada há ${Math.abs(diff)} dia${Math.abs(diff) === 1 ? '' : 's'}`;
  if (diff === 0) return 'Vence hoje';
  if (diff === 1) return 'Vence amanhã';
  if (diff <= 14) return `Vence em ${diff} dias`;
  return `Vence em ${formatDate(isoDate)}`;
}

export const STATUS_LABEL: Record<ActionStatus, string> = {
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
};

export const PRIORITY_LABEL: Record<ActionPriority, string> = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

export const PRIORITY_CLASS: Record<ActionPriority, string> = {
  urgente: 'badge-priority-urgent',
  alta: 'badge-priority-high',
  media: 'badge-priority-medium',
  baixa: 'badge-priority-low',
};

export const EVENT_TYPE_LABEL: Record<CalendarEventType, string> = {
  efemeride: 'Efeméride',
  turne: 'Turnê',
  acao_interna: 'Ação interna',
  outro: 'Outro',
};

export const METRIC_LABEL: Record<string, string> = {
  followers: 'Seguidores',
  posts_count: 'Publicações',
  views: 'Visualizações (30d)',
  reach: 'Alcance (30d)',
  reach_daily: 'Alcance (dia)',
  interactions: 'Interações (30d)',
  accounts_engaged: 'Contas engajadas (30d)',
  profile_visits: 'Visitas ao perfil (30d)',
  external_link_taps: 'Toques no link (30d)',
  page_fans: 'Fãs da página',
  organic_impressions: 'Impressões orgânicas',
  post_engagements: 'Engajamentos em posts',
  video_views: 'Views de vídeo',
};

export const metricLabel = (metric: string): string => METRIC_LABEL[metric] ?? metric;

export const PLATFORM_LABEL: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
};
